import React, { useEffect } from 'react';
import '../styles/globals.scss';
import Layout from '../components/Layout';
import Head from 'next/head';
import initAuth from '../firebase/initAuth'; // the module you created above
import { startAnalytics } from '../firebase/firebaseApp';
import { readConsent, needsOptIn } from '../lib/consent';
import { captureAttribution } from '../lib/attribution';

import { useRouter } from 'next/router'; // Import to get the route
import "../styles/fonts.css";
import localFont from 'next/font/local';

// Latin subsets from Google Fonts, sized to match the fonts they replaced
// (Stellar Sans, Supply Mono, Editorial Old) so layouts keep their dimensions.
// Archivo keeps its width axis (font-stretch is set in globals.scss) and is
// capped at 700, like Stellar Bold.
const archivo = localFont({
  src: '../styles/fonts/archivo.woff2',
  weight: '100 700',
  declarations: [
    { prop: 'size-adjust', value: '102%' },
    { prop: 'font-stretch', value: '62% 125%' },
  ],
  display: 'swap',
});
const martianMono = localFont({
  src: '../styles/fonts/martian-mono.woff2',
  weight: '100 800',
  declarations: [{ prop: 'size-adjust', value: '86%' }],
  display: 'swap',
});
const instrumentSerif = localFont({
  src: [
    { path: '../styles/fonts/instrument-serif.woff2', weight: '400', style: 'normal' },
    { path: '../styles/fonts/instrument-serif-italic.woff2', weight: '400', style: 'italic' },
  ],
  declarations: [{ prop: 'size-adjust', value: '112%' }],
  display: 'swap',
});

initAuth();

function MyApp({ Component, pageProps }) {
  const router = useRouter();
  const isEmbedRoute = router.pathname.startsWith('/embed');

  // Analytics runs unless the visitor rejected it in the CookieBanner (Layout); in the EU it
  // waits for Accept. The second readConsent catches a choice made while the country loads.
  // Note the ad, campaign or site that brought this visitor, so a waitlist signup can be credited to it.
  useEffect(() => { captureAttribution(); }, []);

  useEffect(() => {
    const consent = readConsent();
    if (consent === 'granted') startAnalytics();
    else if (consent === null) needsOptIn().then(optIn => {
      if (!optIn && readConsent() === null) startAnalytics();
    });
  }, []);

  return (
    <>
      <style jsx global>{`
        :root {
          --font-archivo: ${archivo.style.fontFamily};
          --font-martian-mono: ${martianMono.style.fontFamily};
          --font-instrument-serif: ${instrumentSerif.style.fontFamily};
        }
      `}</style>
      <Head>
        {/* Tabs read "Monet" unless a page sets its own title for search (home, pricing, blog
            posts); every page keeps its own og:title for social previews. */}
        <title key="title">Monet</title>
        <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
        <meta name="description" content="Monet is software for event designers to speed up their creative process. Lay out a show floor inside your real venue, to scale, and export DWG, PDF or GLB." />
        <meta name="viewport" content="width=device-width,minimum-scale=1, initial-scale=1" />
      </Head>
      {isEmbedRoute ?
        <Component {...pageProps} /> :
        <Layout>
          <Component {...pageProps} />
        </Layout>
      }
    </>
  );
}

export default MyApp;
